import { config } from "@fidget/common/config/config";
import { debounce } from "lodash";
import React, { createContext, useContext, useEffect, useState } from "react";

export enum WindowSize {
  Default = 0,
  Small = 1200
}

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
};

export const useWindowDimensionsState = () => {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    const handleResize = debounce(
      () => setWindowDimensions(getWindowDimensions()),
      config.services.times.resizeBrowserDebounceMillis
    );

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getWindowSize = (): WindowSize => {
    if (windowDimensions.width <= WindowSize.Small) {
      return WindowSize.Small;
    }
    return WindowSize.Default;
  };

  return { ...windowDimensions, windowSize: getWindowSize() };
};

export interface WindowDimensions {
  height: number;
  width: number;
  windowSize: WindowSize;
}

export const WindowDimensionsContext = createContext<WindowDimensions | undefined>(undefined);
WindowDimensionsContext.displayName = "WindowDimensionsContext"; // for name to display in dev tools

export const useWindowDimensions = (): WindowDimensions => {
  const context = useContext(WindowDimensionsContext);
  if (!context) {
    throw Error("useWindowDimensions must be used within WindowDimensionsContextProvider");
  }

  return context;
};

export const WindowDimensionsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const values = useWindowDimensionsState();

  return (
    <WindowDimensionsContext.Provider value={values}>{children}</WindowDimensionsContext.Provider>
  );
};
